"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { auth, db } from "@/firebase/config";
import {
  doc, getDoc, collection, query, where, orderBy, onSnapshot,
} from "@/lib/firestore-instrumented";
import { CardMedia } from "@/components/card-primitives";
import { fetchDiscussionDetail } from "@/lib/discussionDetailClient";

function useCurrentUser() {
  const [u, setU] = useState(() => auth.currentUser);
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(setU);
    return () => unsub();
  }, []);
  return u;
}

export default function ThreadClient({ slug, threadId, initialThread = null }) {
  const [thread, setThread] = useState(initialThread);
  const [archivedReplies, setArchivedReplies] = useState([]);
  const [error, setError] = useState("");
  const user = useCurrentUser();

  // Load thread once
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "discussions", slug, "threads", threadId));
        if (snap.exists()) {
          setThread({ id: snap.id, ...snap.data() });
          return;
        }
        const detail = await fetchDiscussionDetail({ hubSlug: slug, threadId, user });
        setThread(detail.thread);
        setArchivedReplies(detail.replies || []);
      } catch (e) { console.error(e); setError("Failed to load thread."); }
    })();
  }, [slug, threadId, user]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!thread) return <p className="text-gray-500">Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="rounded border p-4">
        <h2 className="text-xl font-semibold">{thread.text}</h2>
        <p className="text-xs text-gray-500">
          Created {thread.createdAt?.toDate && thread.createdAt.toDate().toLocaleString()}
        </p>
        <CardMedia
          items={thread.images || []}
          maxHeight={520}
          variant={(thread.images || []).length > 1 ? "carousel" : "single"}
        />
      </div>

      {thread?._archiveState === "archived" ? (
        <ArchivedReplies replies={archivedReplies} />
      ) : (
        <RootReplies slug={slug} threadId={threadId} />
      )}
    </div>
  );
}

function ArchivedReplies({ replies }) {
  const byParent = useMemo(() => {
    const grouped = new Map();
    for (const reply of replies || []) {
      const key = reply.parentId || "root";
      grouped.set(key, [...(grouped.get(key) || []), reply]);
    }
    return grouped;
  }, [replies]);

  const renderLevel = (parentId = "root", depth = 0) => (byParent.get(parentId) || []).map((reply) => (
    <div key={reply.id} className="border-l border-[var(--border)] pl-3" style={{ marginLeft: Math.min(depth, 5) * 12 }}>
      <div className="whitespace-pre-wrap">{reply.body}</div>
      <CardMedia
        items={reply.images || []}
        maxHeight={420}
        variant={(reply.images || []).length > 1 ? "carousel" : "single"}
      />
      <div className="mt-1 text-xs text-[var(--muted)]">Archived reply</div>
      <div className="mt-3 space-y-3">{renderLevel(reply.id, depth + 1)}</div>
    </div>
  ));

  return (
    <section className="space-y-4 rounded border border-[var(--border)] p-4">
      <div>
        <h3 className="font-semibold">Replies</h3>
        <p className="text-sm text-[var(--muted)]">This discussion is archived and read-only.</p>
      </div>
      {replies.length ? renderLevel() : <p className="text-sm text-[var(--muted)]">No replies.</p>}
    </section>
  );
}

function RootReplies({ slug, threadId }) {
  const [roots, setRoots] = useState([]);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const user = useCurrentUser();

  // Live root replies: parentId == null (requires composite index on (parentId, createdAt) in replies CG)
  useEffect(() => {
    const q = query(
      collection(db, "discussions", slug, "threads", threadId, "replies"),
      where("parentId", "==", null),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setRoots(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (e) => { console.error(e); setError("Failed to load replies."); }
    );
    return () => unsub();
  }, [slug, threadId]);

  const postRoot = useCallback(async () => {
    const u = auth.currentUser;
    const text = body.trim();
    if (!u || !text) return;
    setPosting(true); setError("");
    try {
      const idToken = await u.getIdToken(true);
      const resp = await fetch(process.env.NEXT_PUBLIC_CREATE_REPLY_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken, hubSlug: slug, threadId, body: text, parentId: null }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      setBody("");
    } catch (e) { console.error(e); setError(e.message || "Could not post reply."); }
    finally { setPosting(false); }
  }, [slug, threadId, body]);

  return (
    <div className="space-y-5">
      <div className="rounded border p-4 space-y-3">
        <h3 className="font-semibold">Replies</h3>
        {roots.length === 0 && <p className="text-sm text-gray-500">Be the first to reply.</p>}
        <div className="space-y-4">
          {roots.map(r => (
            <ReplyItem key={r.id} reply={r} slug={slug} threadId={threadId} depth={0} />
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a reply…"
          rows={3}
          className="flex-1 border rounded px-3 py-2"
        />
        <button
          onClick={postRoot}
          disabled={posting || !body.trim() || !user}
          className="h-min px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
        >
          {posting ? "Posting…" : "Reply"}
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function ReplyItem({ reply, slug, threadId, depth }) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState([]);
  const [loadingKids, setLoadingKids] = useState(false);
  const [err, setErr] = useState("");
  const [showBox, setShowBox] = useState(false);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const user = useCurrentUser();

  // Live-load children first time we expand (requires same (parentId, createdAt) index)
  useEffect(() => {
    if (!open) return;
    setLoadingKids(true);
    setErr("");
    const q = query(
      collection(db, "discussions", slug, "threads", threadId, "replies"),
      where("parentId", "==", reply.id),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setChildren(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingKids(false);
      },
      (e) => { console.error(e); setErr("Failed to load child replies."); setLoadingKids(false); }
    );
    return () => unsub();
  }, [open, slug, threadId, reply.id]);

  const postChild = useCallback(async () => {
    const u = auth.currentUser;
    const text = body.trim();
    if (!u || !text) return;
    setPosting(true); setErr("");
    try {
      const idToken = await u.getIdToken(true);
      const resp = await fetch(process.env.NEXT_PUBLIC_CREATE_REPLY_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken, hubSlug: slug, threadId, body: text, parentId: reply.id }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      setBody("");
      setShowBox(false);
      if (!open) setOpen(true);
    } catch (e) { console.error(e); setErr(e.message || "Could not post reply."); }
    finally { setPosting(false); }
  }, [slug, threadId, body, reply.id, open]);

  return (
    <div style={{ marginLeft: depth ? Math.min(depth, 5) * 16 : 0 }} className="border-l pl-3">
      <div className="whitespace-pre-wrap">{reply.body}</div>
      <div className="text-xs text-gray-500 mt-1 flex gap-3 items-center">
        {reply.createdAt?.toDate && reply.createdAt.toDate().toLocaleString()}
        <button className="underline" onClick={() => setShowBox(v => !v)} disabled={!user}>Reply</button>
        {reply.childCount > 0 && (
          <button className="underline" onClick={() => setOpen(v => !v)}>
            {open ? "Hide" : `View replies (${reply.childCount})`}
          </button>
        )}
      </div>

      {showBox && (
        <div className="mt-2 flex gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            className="flex-1 border rounded px-3 py-2"
            placeholder="Reply to this comment…"
          />
          <button
            onClick={postChild}
            disabled={posting || !body.trim() || !user}
            className="h-min px-3 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
          >
            {posting ? "Posting…" : "Reply"}
          </button>
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-4">
          {loadingKids && <p className="text-sm text-gray-500">Loading…</p>}
          {children.map(c => (
            <ReplyItem key={c.id} reply={c} slug={slug} threadId={threadId} depth={(reply.depth || 0) + 1} />
          ))}
          {children.length === 0 && !loadingKids && (
            <p className="text-sm text-gray-500">No replies yet.</p>
          )}
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
      )}
    </div>
  );
}
